import { useEffect, useRef } from "react";
import "./NightSlashAnimation.css";

export default function NightSlashAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for defender
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of defender sprite
    const frameWidth = 192;  // Width of each frame
    const frameHeight = 192; // Height of the sprite
    const centerX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const centerY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Track all created elements for cleanup
    const elements = [];
    const timers = [];

    // Initial attacker animation - dark energy buildup
    attackerEl.style.transition = 'transform 0.3s ease-in-out, filter 0.3s ease-in-out';
    attackerEl.style.transform = 'scale(1.15)';
    attackerEl.style.filter = 'drop-shadow(0 0 10px #4a0080)';
    
    // Create dark energy buildup around attacker
    const darkAura = document.createElement('div');
    darkAura.className = 'night-slash-aura';
    const attackerRect = attackerEl.getBoundingClientRect();
    const attackerCenterX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerCenterY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    darkAura.style.left = `${attackerCenterX}px`;
    darkAura.style.top = `${attackerCenterY}px`;
    container.appendChild(darkAura);
    elements.push(darkAura);
    
    // Create main night slash animation
    const nightSlashTimer = setTimeout(() => {
      // Create multiple dark slash marks
      for (let i = 0; i < 3; i++) {
        const slashTimer = setTimeout(() => {
          const slash = document.createElement("div");
          slash.className = "night-slash-mark";
          
          // Position each slash slightly offset
          const offsetX = (i - 1) * 20;
          const offsetY = (i - 1) * 15;
          slash.style.setProperty('--sx', `${centerX - frameWidth / 2 + offsetX}px`);
          slash.style.setProperty('--sy', `${centerY - frameHeight / 2 + offsetY}px`);
          slash.style.setProperty('--slash-delay', `${i * 0.1}s`);
          slash.style.setProperty('--slash-rotation', `${-15 + i * 15}deg`);
          
          container.appendChild(slash);
          elements.push(slash);
          animRef.current = slash;
          
          // Auto-remove slash after animation
          const removeTimer = setTimeout(() => {
            if (slash.parentNode) {
              slash.remove();
            }
          }, 1000);
          timers.push(removeTimer);
        }, i * 150); // Staggered slash attacks
        
        timers.push(slashTimer);
      }
    }, 300); // After dark aura buildup
    
    timers.push(nightSlashTimer);

    // Impact effects and critical hit potential
    const impactTimer = setTimeout(() => {
      // Dark energy explosion on impact
      const explosion = document.createElement('div');
      explosion.className = 'night-slash-explosion';
      explosion.style.left = `${centerX}px`;
      explosion.style.top = `${centerY}px`;
      container.appendChild(explosion);
      elements.push(explosion);
      
      // Critical hit indicator (random chance for visual effect)
      const critChance = Math.random();
      if (critChance < 0.3) { // 30% visual crit indicator
        const critEffect = document.createElement('div');
        critEffect.className = 'night-slash-critical';
        critEffect.style.left = `${centerX}px`;
        critEffect.style.top = `${centerY - 50}px`;
        container.appendChild(critEffect);
        elements.push(critEffect);
        
        // Enhanced explosion for crit
        explosion.classList.add('critical-explosion');
      }
      
      // Make defender react with dark energy
      defenderEl.classList.add('night-slash-hit');
      defenderEl.style.filter = 'brightness(0.3) contrast(1.5)';
      
      // Create dark particles
      for (let i = 0; i < 12; i++) {
        const particle = document.createElement('div');
        particle.className = 'night-slash-particle';
        
        // Position around the impact point
        const angle = Math.random() * Math.PI * 2;
        const distance = 10 + Math.random() * 35;
        particle.style.left = `${centerX + Math.cos(angle) * distance}px`;
        particle.style.top = `${centerY + Math.sin(angle) * distance}px`;
        
        // Random properties
        particle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
        particle.style.setProperty('--scale', `${0.5 + Math.random() * 1}`);
        particle.style.setProperty('--drift-x', `${(Math.random() - 0.5) * 40}px`);
        particle.style.setProperty('--drift-y', `${(Math.random() - 0.5) * 40}px`);
        
        container.appendChild(particle);
        elements.push(particle);
        
        // Auto-remove particles
        const particleTimer = setTimeout(() => {
          if (particle.parentNode) {
            particle.remove();
          }
        }, 600 + Math.random() * 300);
        timers.push(particleTimer);
      }
      
      // Create lingering dark slash marks
      for (let i = 0; i < 2; i++) {
        const mark = document.createElement('div');
        mark.className = 'night-slash-lingering-mark';
        mark.style.left = `${centerX + (i - 0.5) * 30}px`;
        mark.style.top = `${centerY + (i - 0.5) * 20}px`;
        mark.style.setProperty('--mark-rotation', `${-30 + i * 60}deg`);
        container.appendChild(mark);
        elements.push(mark);
        
        // Auto-remove marks
        const markTimer = setTimeout(() => {
          if (mark.parentNode) {
            mark.remove();
          }
        }, 1200);
        timers.push(markTimer);
      }
      
      // Reset defender after hit effect
      const defenderResetTimer = setTimeout(() => {
        defenderEl.classList.remove('night-slash-hit');
        defenderEl.style.filter = '';
      }, 400);
      timers.push(defenderResetTimer);
      
    }, 500); // Impact timing
    
    timers.push(impactTimer);

    // Reset attacker position after animation
    const resetTimer = setTimeout(() => {
      attackerEl.style.transition = 'transform 0.3s ease-out, filter 0.3s ease-out';
      attackerEl.style.transform = '';
      attackerEl.style.filter = '';
    }, 600);
    timers.push(resetTimer);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 1400);
    timers.push(completeTimer);

    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset attacker
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
        attackerEl.style.filter = '';
      }
      
      // Reset defender
      if (defenderEl) {
        defenderEl.classList.remove('night-slash-hit');
        defenderEl.style.filter = '';
      }
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Remove any remaining particles or effects
      const remainingElements = container.querySelectorAll(
        '.night-slash-aura, .night-slash-mark, .night-slash-explosion, ' +
        '.night-slash-critical, .night-slash-particle, .night-slash-lingering-mark'
      );
      remainingElements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

NightSlashAnimation.moveType = "dark";
